"use client";

import { FormEvent, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { loginWithEmailPassword } from '@/lib/auth-api-client';
import ForgotPasswordForm from './ForgotPasswordForm';
import { SectionDivider } from '@/components/ui/SectionDivider';
import { SocialAuthButtons } from './SocialAuthButtons';
// import { handleXOAuth } from '@/app/auth/actions'; // Temporarily disabled due to GoTrue Twitter OAuth bug

interface LoginFormProps {
  onToggleForm: () => void;
  onSuccess?: () => void;
  allowPublicSignup?: boolean;
  /**
   * Optional internal path (starts with `/`) to land on after OAuth completes.
   * Used when the CTA routed the user through `?login=true&redirect_to=...`.
   * Must be pre-sanitized by the caller; the server re-validates on the
   * OAuth callback via `cookieManager.setRedirectPath`.
   */
  redirectTo?: string;
}

export default function LoginForm({ onToggleForm, onSuccess, allowPublicSignup = false, redirectTo }: LoginFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [oauthPending, setOAuthPending] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // const [xOAuthLoading, setXOAuthLoading] = useState(false); // Temporarily disabled due to GoTrue Twitter OAuth bug
  const oauthError = searchParams.get('error');

  useEffect(() => {
    if (oauthError) {
      const errorMessages = {
        // OAuth session errors
        'oauth_session_failed': 'Social sign-in session failed. Please try again or use email/password below.',
        'cookie_error': 'Authentication setup failed. Please clear your browser cookies and try again.',
        'callback_processing_failed': 'Social sign-in processing failed. Please try again or use email/password below.',
        'callback_api_error': 'Social sign-in encountered an error. Please try again or use email/password below.',
        
        // Enhanced error messages for specific failure types
        'profile_sync_failed': 'Failed to create your profile. Please try signing in again or contact support.',
        'oauth_token_invalid': 'Social sign-in session expired. Please try signing in again.',
        'session_setup_failed': 'Failed to establish session. Please clear your browser cookies and try again.',
        'callback_network_error': 'Network error during sign-in completion. Please check your connection and try again.',
        
        // Network and service errors
        'network_error': 'Network connection failed. Please check your internet connection and try again.',
        'service_unavailable': 'Authentication service is temporarily unavailable. Please try again in a few moments.',
        'configuration_error': 'Social sign-in configuration error. Please use email/password login below or contact support.',
        'rate_limit_exceeded': 'Too many sign-in attempts. Please wait a few minutes before trying again.'
      };
      
      toast.error(errorMessages[oauthError as keyof typeof errorMessages] || 'Social sign-in failed. Please try email/password login below.');
      
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [oauthError]);

  /* Temporarily disabled due to GoTrue Twitter OAuth bug
  const handleXSignIn = async () => {
    setXOAuthLoading(true);
    try {
      console.log('[X OAuth] Starting X OAuth flow');
      const result = await handleXOAuth();
      console.log('[X OAuth] Server response:', result);
      
      if (result.success) {
        if (result.redirectTo) {
          console.log('[X OAuth] Redirecting to:', result.redirectTo);
          window.location.href = result.redirectTo;
        } else if (onSuccess) {
          console.log('[X OAuth] Calling onSuccess callback');
          onSuccess();
        } else {
          console.log('[X OAuth] Refreshing page');
          router.refresh();
        }
      } else {
        console.error('[X OAuth] Server returned error:', result.message);
        toast.error(result.message || 'X sign-in failed. Please try again.');
      }
    } catch (error) {
      console.error('[X OAuth] Client error:', error);
      toast.error('An unexpected error occurred with X sign-in.');
    } finally {
      setXOAuthLoading(false);
    }
  };
  */

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(event.currentTarget);
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;

      // Call API client function instead of server action
      const result = await loginWithEmailPassword(email, password);

      if (result.success) {
        if (onSuccess) {
          // Let the parent component handle the success (close modal, refresh, etc.)
          onSuccess();
        } else {
          // Default behavior when no onSuccess handler is provided
          if (result.redirectTo) {
            router.push(result.redirectTo);
          } else {
            router.refresh();
          }
        }
      } else {
        // Handle login failure
        if (result.redirectTo) {
          // Special case: redirect for disabled accounts
          if (onSuccess) {
            // Close the sheet first
            onSuccess();
          }
          router.push(result.redirectTo);
        } else {
          // Normal login failure: show error toast with enhanced error handling
          const errorMessage = result.message || 'Login failed. Please try again.';
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      // Handle unexpected errors
      console.error('Unexpected error during login:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show forgot password form if requested
  if (showForgotPassword) {
    return <ForgotPasswordForm onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  return (
    <Card className="w-full max-w-md p-6 space-y-4">
      <div className="space-y-4">
        <SocialAuthButtons
          redirectTo={redirectTo}
          onSuccess={() => (onSuccess ? onSuccess() : router.refresh())}
          disabled={isLoading}
          onPendingChange={setOAuthPending}
        />

        {/* X OAuth temporarily disabled due to GoTrue bug where it redirects to localhost instead of Twitter's OAuth endpoint
            Implementation is correct per Supabase docs but GoTrue v2.177.0 fails to generate proper Twitter OAuth URL */}
        {/* <Button
          onClick={handleXSignIn}
          className="w-full bg-black text-white border border-gray-700 hover:bg-gray-900 flex items-center justify-center gap-3 font-medium"
          disabled={isLoading || oauthPending}
          type="button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          Continue with X
        </Button> */}
        
        <SectionDivider spacing="sm" className="!px-0 !max-w-none" />
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-[var(--text-primary)]">Email</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              placeholder="Enter your email"
              className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
              autoComplete="email"
              required
              disabled={isLoading || oauthPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-[var(--text-primary)]">Password</Label>
            <Input
              id="login-password"
              name="password"
              type="password"
              placeholder="Enter your password"
              className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
              autoComplete="current-password"
              required
              disabled={isLoading || oauthPending}
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-[var(--secondary-color)] text-[var(--input-text-color)] hover:text-[var(--input-text-color)] hover:bg-[var(--secondary-color)] hover:opacity-80"
            disabled={isLoading || oauthPending}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </div>
      </form>
      
      <div className="text-center">
        <button
          onClick={() => setShowForgotPassword(true)}
          className="text-sm text-blue-400 hover:text-blue-300 hover:underline"
          disabled={isLoading || oauthPending}
        >
          Forgot your password?
        </button>
      </div>

      {allowPublicSignup && (
        <div className="text-center">
          <button
            onClick={onToggleForm}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Don&apos;t have an account? Register
          </button>
        </div>
      )}
    </Card>
  );
}
